<script setup lang="ts">
import { useI18n } from '@/i18n';
import { resolveSubscriptionPeriodEnd, useBillingStore } from '@/stores/billing';
import { useBillingUtils } from '@/use/billingUtils';
import { usePauseAvailability } from '@/use/pauseAvailability';
import {
  Alert,
  Button,
  Divider,
  Heading,
  Input,
  LayoutGap,
  Loader,
  Prose,
  Radio,
  Textarea,
  useConfirmStore,
} from '@dabble/quillui';
import posthog from 'posthog-js';
import { computed, ref, watch } from 'vue';
import BillingPauseSubscriptionForm from '@/components/BillingPauseSubscriptionForm/BillingPauseSubscriptionForm.vue';

// Customer-facing promotion codes, NOT `promo_…` ids. `POST /billing/coupons/:code` resolves its
// argument with `stripe.promotionCodes.list({ code })`, which matches the customer-facing string
// only. A `promo_…` id matches nothing, so from the Vue rewrite (which swapped the ids in) until
// DAB-1303 every accept came back COUPON_INVALID and no discount ever reached Stripe. These are
// the "Exit Survey Special Offer" codes: monthly is 50% off for three months (coupon duRF5o6N).
// Yearly is 12.5% off for twelve months (coupon terms below): applied mid-year, its window holds
// exactly one bill, the next renewal, and a whole year is a coupon annual billing takes as it is.
const MONTHLY_DISCOUNT_CODE = 'THREEMONTHSHALFMONTHLY';
const YEARLY_DISCOUNT_CODE = 'STAYANNUAL';
const YEARLY_DISCOUNT_TERMS = { percent_off: 12.5, duration: 'repeating', duration_in_months: 12 };

const emit = defineEmits<{
  cancel: [];
  'subscription-cancelled': [];
}>();

const { t } = useI18n();
const billingStore = useBillingStore();
const confirmStore = useConfirmStore();
const { formatDecimal, formatPrice, trialCancelConfirmBody, pausedCancelConfirmBody } = useBillingUtils();

// The confirm below is awaited before isLoading is ever set, so without a local latch the submit
// button stays enabled for the whole dialog and an Enter keypress re-fires it. QuillUI's confirm
// store holds a single resolver, so a second call orphans the first promise forever.
const submitting = ref(false);
const selectedReason = ref<string>('');
const otherReasonDetail = ref<string>('');
const missingFeaturesDetail = ref<string>('');
const alternativeName = ref<string>('');
const technicalIssuesDetail = ref<string>('');
const feedback = ref<string>('');
const offerAccepted = ref(false);

const cancellationReasons = computed(() => [
  { value: 'too_expensive', label: t('exit_survey_reason_too_expensive') },
  { value: 'missing_features', label: t('exit_survey_reason_missing_features') },
  { value: 'found_alternative', label: t('exit_survey_reason_found_alternative') },
  { value: 'temporary_pause', label: t('exit_survey_reason_temporary_pause') },
  { value: 'not_using_enough', label: t('exit_survey_reason_not_using') },
  { value: 'technical_issues', label: t('exit_survey_reason_technical') },
  { value: 'other', label: t('exit_survey_reason_other') },
]);

// The composable owns the cooldown-over-yearly precedence shared with the portal's Pause item.
const {
  reason: pauseUnavailableReason,
  cooldownDateFormatted: pauseCooldownDateFormatted,
  isYearlyPlan,
} = usePauseAvailability();

const discountCode = computed(() => (isYearlyPlan.value ? YEARLY_DISCOUNT_CODE : MONTHLY_DISCOUNT_CODE));
// The pitch has to describe the coupon it applies: "50% off for 3 months" is the monthly one, and the
// yearly one comes off the next renewal. One string for both would promise a yearly subscriber
// something Stripe then doesn't do (jacwright review, DAB-1303).
const yearlyPercent = computed(() => formatDecimal(YEARLY_DISCOUNT_TERMS.percent_off));
const offerPitch = computed(() =>
  isYearlyPlan.value
    ? t('exit_survey_offer_pitch_yearly', { percent: yearlyPercent.value })
    : t('exit_survey_offer_pitch'),
);
const offerAccept = computed(() =>
  isYearlyPlan.value
    ? t('exit_survey_offer_accept_yearly', { percent: yearlyPercent.value })
    : t('exit_survey_offer_accept'),
);

// Exhaustive over the reason union: a new reason fails the type check here rather than
// rendering the pause pitch (or an unexplained gap) in the survey.
const pauseUnavailableNotice = computed(() => {
  const reason = pauseUnavailableReason.value;
  if (!reason) return null;
  const notices: Record<typeof reason, string> = {
    pendingCancel: t('cancel_survey_pause_pending_cancel_notice'),
    cooldown: t('billing_pause_cooldown_notice', { date: pauseCooldownDateFormatted.value }),
    yearly: t('cancel_survey_pause_yearly_notice'),
  };
  return notices[reason];
});
const pausePitchAvailable = computed(() => pauseUnavailableReason.value == null);

// An older price that today's comparable plan beats: cancelling gives it up, since it isn't offered
// anymore (DAB-1999). A trial hasn't paid it yet, so it has nothing to lose.
const savings = computed(() =>
  billingStore.isTrialing ? null : (billingStore.primarySubscription?.price_savings ?? null),
);
const savingsYearly = computed(() => savings.value && formatPrice(savings.value.yearly, savings.value.currency, true));
const savingsSummary = computed(() => {
  const s = savings.value;
  const paid = billingStore.primarySubscription?.plan_amount;
  if (!s || paid == null) return '';
  return t(s.interval === 'year' ? 'exit_survey_savings_yearly' : 'exit_survey_savings_monthly', {
    price: formatPrice(paid, s.currency, true),
    plan: s.plan,
    listed: formatPrice(s.amount, s.currency, true),
    yearly: savingsYearly.value,
  });
});

const isReasonSelected = computed(() => {
  if (!selectedReason.value) return false;
  if (selectedReason.value === 'other') {
    return otherReasonDetail.value.trim().length > 0;
  }
  return true;
});

// Set when submit is pressed with no reason chosen, cleared as soon as the user picks one. The
// submit button used to be `disabled` until a reason was selected, and a disabled button is
// indistinguishable from a broken one: it dispatches no click, so the user gets no explanation
// and we get no autocapture — "the cancel button doesn't work" with nothing in PostHog to show
// for it (DAB-910). Now the click lands, the live region says what is missing, and the miss is
// counted.
//
// The `cancel_survey_reason_missing` event fires on EVERY press, with `press` counting up while
// the prompt is on screen and resetting when it clears. So `press = 1` is one event per person
// per genuine miss — the successor to the "42% never picked a reason" figure — and `press > 1`
// is a user pressing again with the prompt already showing, i.e. the prompt is not landing.
// Pressing repeatedly is exactly what a stuck user does, so a per-press count is the signal;
// it just has to be readable without remembering to go through unique users (#225 review).
const reasonMissing = ref(false);
const reasonMissingPresses = ref(0);
watch([selectedReason, otherReasonDetail], () => {
  reasonMissing.value = false;
  reasonMissingPresses.value = 0;
});

const handleSubmit = async () => {
  if (billingStore.isLoading || submitting.value) return;
  if (!isReasonSelected.value) {
    reasonMissing.value = true;
    reasonMissingPresses.value += 1;
    // `selected` tells "nothing picked" apart from "Other with an empty detail box".
    posthog.capture('cancel_survey_reason_missing', {
      selected: selectedReason.value || null,
      press: reasonMissingPresses.value,
    });
    return;
  }
  submitting.value = true;
  try {
    await submitCancellation();
  } finally {
    submitting.value = false;
  }
};

const submitCancellation = async () => {
  // Cancelling a trial takes effect immediately (dabble-rest cancels a trialing sub outright so
  // the customer.updated webhook cannot reverse it — DAB-922/DAB-961), which forfeits the rest of
  // the trial and closes retrials for the cooldown window. Confirming here makes it a deliberate
  // two-step action — pick a reason, then acknowledge what is lost — rather than one click.
  // This is the reachable cancel path: BillingPortalView.goToExitSurvey() opens THIS component in
  // a modal. (ExitSurveyView is a near-duplicate at /billing/exit-survey that nothing links to.)
  if (billingStore.isTrialing) {
    const confirmed = await confirmStore.confirm(
      t('billing_cancel_confirm_trial_title'),
      trialCancelConfirmBody(billingStore.primarySubscription?.trial_end),
      {
        cancel: { text: t('billing_keep_it') },
        confirm: { text: t('billing_cancel_trial'), variant: 'danger' },
      },
    );
    if (!confirmed) return;
  } else if (billingStore.isPausedSubscription) {
    // A paused subscription reaches neither branch above: a native pause_collection sub stays
    // `status: 'active'` so isTrialing is false, and isTrialing deliberately excludes a legacy
    // pause (which parks the sub on a trial whose trial_end is the *resume* date, so trial copy
    // would name a date that isn't a trial end). Without this it is the one cancel path left with
    // no confirmation — and it is the path DAB-922's reporter took.
    //
    // What cancelling does depends on the subscription's status, and the body must say which.
    // This mirrors dabble-rest's cancelSubscription exactly (billing.ts, cancelNow):
    //
    //   when === 'now' || status === 'trialing' || (isPaused && !prepaidTimeRemains)
    //
    // `status === 'trialing'` short-circuits before the period is ever consulted, so any paused
    // sub still on that status cancels outright: a legacy is_paused_until pause (parked on a trial
    // whose trial_end is the resume date) and, equally, a native pause_collection on a converted
    // trial that then paused. The immediate line stays true for both and must be shown.
    //
    // Everything else — a native pause on an active sub, or a stale is_paused_until on one —
    // cancels at period end when prepaid time remains and outright only once the period has run
    // out. The old single message ("ends right away, can't resume") rested on the premise that a
    // paused sub has no prepaid time to forfeit; DAB-1288 showed that is false mid-period.
    // pausedCancelConfirmBody mirrors that gate from the same field the server reads.
    //
    // Key on status, not on which pause marker is present: the marker is a proxy that disagrees
    // with rest on both of the shapes above (#217 review, round 2).
    const confirmed = await confirmStore.confirm(
      t('billing_cancel_confirm_subscription_title'),
      billingStore.primarySubscription?.status === 'trialing'
        ? t('billing_cancel_confirm_paused_body')
        : pausedCancelConfirmBody(
            billingStore.primarySubscription
              ? resolveSubscriptionPeriodEnd(billingStore.primarySubscription)
              : undefined,
          ),
      {
        cancel: { text: t('billing_keep_it') },
        confirm: { text: t('billing_cancel_subscription', 'Cancel Subscription'), variant: 'danger' },
      },
    );
    if (!confirmed) return;
  } else if (savings.value && savingsYearly.value) {
    const confirmed = await confirmStore.confirm(
      t('billing_cancel_confirm_savings_title'),
      t('billing_cancel_confirm_savings_body', { yearly: savingsYearly.value }),
      {
        cancel: { text: t('billing_keep_my_price') },
        confirm: { text: t('billing_cancel_subscription', 'Cancel Subscription'), variant: 'danger' },
      },
    );
    posthog.capture('cancel_survey_savings_confirm', {
      kept: !confirmed,
      plan: savings.value.plan,
      plan_interval: savings.value.interval,
      yearly_savings: savings.value.yearly / 100,
      currency: savings.value.currency,
    });
    if (!confirmed) return;
  }

  const trimmed = (s: string) => s.trim() || undefined;

  const success = await billingStore.cancelSubscription({
    reason: selectedReason.value,
    feedback: trimmed(feedback.value),
    otherReason: selectedReason.value === 'other' ? trimmed(otherReasonDetail.value) : undefined,
    missingFeatures: selectedReason.value === 'missing_features' ? trimmed(missingFeaturesDetail.value) : undefined,
    alternativeName: selectedReason.value === 'found_alternative' ? trimmed(alternativeName.value) : undefined,
    technicalIssues: selectedReason.value === 'technical_issues' ? trimmed(technicalIssuesDetail.value) : undefined,
  });

  if (success) {
    setTimeout(() => {
      emit('subscription-cancelled');
    }, 500);
  }
};

async function handleAcceptOffer() {
  if (billingStore.isLoading || offerAccepted.value) return;
  const ok = await billingStore.applyCouponCode(discountCode.value);
  // Record the outcome, not just the click. This offer failed for every taker for four months
  // and nothing on our side counted it — the only trace was Stripe's own request log (DAB-1303).
  // `error` is the API code, so a breakdown reads COUPON_INVALID / COUPON_EXPIRED, not a toast.
  posthog.capture('cancel_survey_save_offer', {
    outcome: ok ? 'applied' : 'failed',
    error: ok ? null : billingStore.lastApplyErrorCode,
    code: discountCode.value,
    plan_interval: isYearlyPlan.value ? 'year' : 'month',
  });
  if (ok) {
    offerAccepted.value = true;
    setTimeout(() => {
      emit('subscription-cancelled');
    }, 500);
  }
}

function handlePausedInstead() {
  emit('subscription-cancelled');
}
</script>

<template>
  <form @submit.prevent="handleSubmit">
    <LayoutGap direction="column" size="medium">
      <Heading :level="2" align="center" :margin="false">{{ t('exit_survey_title') }}</Heading>
      <Prose variant="secondary" align="center">{{ t('exit_survey_subheader') }}</Prose>

      <Alert v-if="savingsSummary" variant="warning" :icon="false">
        <LayoutGap direction="column" size="xsmall">
          <Prose weight="semibold">{{ t('exit_survey_savings_title', { yearly: savingsYearly }) }}</Prose>
          <Prose>{{ savingsSummary }}</Prose>
          <Prose>{{ t('exit_survey_savings_lost') }}</Prose>
        </LayoutGap>
      </Alert>

      <LayoutGap direction="row" size="medium" wrap class="cancel-survey__columns">
        <LayoutGap direction="column" size="small" class="cancel-survey__reasons">
          <Prose weight="semibold">{{ t('exit_survey_reason_label') }}</Prose>
          <LayoutGap direction="column" size="xsmall">
            <Radio
              v-for="reason in cancellationReasons"
              :key="reason.value"
              v-model="selectedReason"
              :value="reason.value"
              :label="reason.label"
              name="cancellationReason"
            />
          </LayoutGap>
        </LayoutGap>

        <LayoutGap direction="column" size="small" class="cancel-survey__detail">
          <Textarea
            v-if="selectedReason === 'other'"
            v-model="otherReasonDetail"
            :label="t('exit_survey_reason_other')"
            :placeholder="t('exit_survey_other_placeholder')"
            :rows="3"
            resize="vertical"
          />

          <Alert v-if="selectedReason === 'too_expensive' && !offerAccepted" variant="info" :icon="false">
            <LayoutGap direction="column" size="small">
              <Prose>{{ offerPitch }}</Prose>
              <LayoutGap direction="row" align="left" size="small">
                <Button type="button" variant="primary" :disabled="billingStore.isLoading" @click="handleAcceptOffer">
                  {{ offerAccept }}
                </Button>
              </LayoutGap>
            </LayoutGap>
          </Alert>

          <Alert v-if="selectedReason === 'too_expensive' && offerAccepted" variant="success">
            {{ t('exit_survey_offer_applied') }}
          </Alert>

          <Textarea
            v-if="selectedReason === 'missing_features'"
            v-model="missingFeaturesDetail"
            :label="t('exit_survey_missing_features_label')"
            :placeholder="t('exit_survey_missing_features_placeholder')"
            :rows="3"
            resize="vertical"
          />

          <Input
            v-if="selectedReason === 'found_alternative'"
            v-model="alternativeName"
            type="text"
            :label="t('exit_survey_alternative_label')"
            :placeholder="t('exit_survey_alternative_placeholder')"
          />

          <Textarea
            v-if="selectedReason === 'technical_issues'"
            v-model="technicalIssuesDetail"
            :label="t('exit_survey_technical_label')"
            :placeholder="t('exit_survey_technical_placeholder')"
            :rows="3"
            resize="vertical"
          />

          <LayoutGap v-if="selectedReason === 'not_using_enough'" direction="column" size="small">
            <Alert v-if="pauseUnavailableNotice" variant="info">
              {{ pauseUnavailableNotice }}
            </Alert>
            <!-- The pause pitch hides the survey's own action row, so its "never mind" button is the
                 only way out: it keeps the plan and closes the survey, like the one it replaces
                 (DAB-579). -->
            <template v-else>
              <Prose>{{ t('exit_survey_pause_pitch') }}</Prose>
              <BillingPauseSubscriptionForm
                from-cancel-survey
                @pause-subscription="handlePausedInstead"
                @back-to-billing="emit('cancel')"
                @cancel="handleSubmit"
              />
            </template>
          </LayoutGap>

          <Textarea
            v-if="selectedReason && selectedReason !== 'not_using_enough' && selectedReason !== 'other'"
            v-model="feedback"
            :label="t('exit_survey_feedback_label')"
            :placeholder="t('exit_survey_feedback_placeholder')"
            :rows="4"
            resize="vertical"
          />
        </LayoutGap>
      </LayoutGap>

      <!-- The reason prompt goes first and is not chained to the store error: `billingStore.error`
           is store-wide and nothing clears it when this form mounts, so a coupon that failed on
           the portal a minute ago would otherwise sit here and swallow the prompt — a silent
           submit again, just with an event attached. -->
      <div id="cancel-survey-error" role="alert" aria-live="assertive" aria-atomic="true">
        <Alert v-if="reasonMissing" variant="warning">
          {{ t('exit_survey_reason_required') }}
        </Alert>
        <Alert v-else-if="billingStore.error" variant="danger">
          {{ billingStore.error }}
        </Alert>
      </div>
    </LayoutGap>

    <div v-if="selectedReason !== 'not_using_enough' || !pausePitchAvailable" class="cancel-survey__actions">
      <Divider />
      <LayoutGap direction="row" align="space-between" vertical-align="center" wrap size="small">
        <Button
          type="button"
          variant="secondary"
          finish="outlined"
          :disabled="billingStore.isLoading"
          @click="emit('cancel')"
        >
          {{ t('exit_survey_keep_subscription') }}
        </Button>
        <LayoutGap v-if="billingStore.isLoading" direction="row" vertical-align="center" size="small">
          <Loader />
          <Prose variant="secondary">{{ t('exit_survey_processing') }}</Prose>
        </LayoutGap>
        <!-- `submitting` covers the window while the trial confirm is open: isLoading is not set
             until after it resolves, so without this the button stays live behind the dialog.
             Deliberately NOT disabled while no reason is chosen — see reasonMissing. -->
        <Button
          v-else
          type="submit"
          variant="danger"
          :disabled="submitting"
          :aria-describedby="reasonMissing ? 'cancel-survey-error' : undefined"
        >
          {{ t('exit_survey_cancel_subscription') }}
        </Button>
      </LayoutGap>
    </div>
  </form>
</template>

<style lang="scss" scoped>
.cancel-survey__columns {
  align-items: flex-start;
}

.cancel-survey__reasons {
  flex: 0 0 18rem;
  min-width: 14rem;

  :deep(label) {
    font-weight: 600;
  }
}

.cancel-survey__detail {
  flex: 1 1 20rem;
  min-width: 16rem;

  :deep(label) {
    font-weight: 600;
  }
}

.cancel-survey__offer {
  padding: 0.75rem;
  border-radius: 0.5rem;
  background-color: rgba(0, 0, 0, 0.03);
}

.cancel-survey__actions {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
</style>
